import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import type { Api, BaseQueryFn } from '@reduxjs/toolkit/query';

// Slices and APIs are owned by their feature. They are imported from their
// concrete module paths (not the feature barrels) so the store never pulls a
// feature's React components into the boot chain.
import authReducer, {
  initializeAuth,
  setCredentials,
  logout,
} from '@features/auth/store/authSlice';
import { authApi } from '@features/auth/api/authApi';
import organizationReducer, {
  initializeActiveOrganization,
  setActiveOrganization,
  clearActiveOrganization,
  resetRejectedOrganizations,
} from '@features/organization/store/organizationSlice';
import { organizationApi } from '@features/organization/api/organizationApi';
import { conversationsApi } from '@features/conversations/api/conversationsApi';

/** Element type of {@link TENANT_SCOPED_APIS}. */
type TenantScopedApi = Api<BaseQueryFn, Record<string, never>, string, string>;

/**
 * Tenant-scoped data APIs whose caches must be wiped when the active
 * organization changes (so a tenant never sees another tenant's cached rows).
 *
 * `authApi` and `organizationApi` are intentionally EXCLUDED: they are
 * user-scoped (the current user / the organizations they may switch into), not
 * tenant-scoped.
 *
 * Every org-scoped API (conversations today; properties, reservations, recapture
 * reporting, … tomorrow) MUST be added here.
 *
 * Each concrete API is cast to {@link TenantScopedApi} on the way in: an RTK
 * Query `Api` specialised with real endpoints is not structurally assignable to
 * the erased `Record<string, never>` endpoints type, but only
 * `util.resetApiState()` (which is endpoint-agnostic) is ever called on these.
 */
const TENANT_SCOPED_APIS: TenantScopedApi[] = [conversationsApi as unknown as TenantScopedApi];

/**
 * Listener middleware for cross-slice side effects (cache resets).
 *
 * Persistence is NOT here: both slices write their own localStorage entries in
 * their reducers, so mounting a slice is all it takes for it to survive a
 * reload. This middleware only owns the effects that span *several* slices.
 */
const listenerMiddleware = createListenerMiddleware();

/**
 * Reset everything scoped to the *current user* — the active-organization
 * selection, the 403-rejection list, the switchable-organizations list
 * (`organizationApi`), and every tenant-scoped data API. Called on BOTH login and
 * logout so that switching accounts in the same browser tab never lets one user
 * inherit another's active organization or see another's cached list. (Security:
 * the backend already 403s cross-tenant access; this prevents even stale NAMES from
 * showing.)
 *
 * `authApi` is the deliberate exclusion: its only cached endpoint is `me`, which
 * describes the caller rather than a tenant, and `setCredentials`/`logout` already
 * replace the auth slice that every consumer reads.
 */
/**
 * Prefix of the per-user, per-tenant inbox filter snapshots written by
 * `usePersistedSearchParams`. Duplicated here rather than imported, for the same
 * reason the storage keys in `baseQuery` are: core must not import from a feature.
 */
const SAVED_FILTERS_PREFIX = 'conversations.filters:';

/**
 * Drop every saved inbox filter snapshot.
 *
 * The keys are already namespaced per user and organization, so a second user on
 * the same browser could never read the first one's filters — but leaving a
 * previous session's saved state on a shared machine is still an answer to a
 * question nobody asked ("what was this person looking at?").
 */
const clearSavedFilters = (): void => {
  try {
    const stale = Object.keys(localStorage).filter((key) => key.startsWith(SAVED_FILTERS_PREFIX));
    for (const key of stale) {
      localStorage.removeItem(key);
    }
  } catch {
    /* ignore — best effort */
  }
};

const resetForUserChange = (dispatch: AppDispatch) => {
  dispatch(clearActiveOrganization());
  dispatch(resetRejectedOrganizations());
  dispatch(organizationApi.util.resetApiState());
  clearSavedFilters();
  for (const tenantApi of TENANT_SCOPED_APIS) {
    dispatch(tenantApi.util.resetApiState());
  }
};

// A new session starts: drop the previous user's tenant state. The login flow
// selects the new active organization AFTER this runs.
listenerMiddleware.startListening({
  actionCreator: setCredentials,
  effect: (_action, api) => resetForUserChange(api.dispatch as AppDispatch),
});

// The session ends: wipe the user- and tenant-scoped caches (see the note above
// on why `authApi` is excluded).
listenerMiddleware.startListening({
  actionCreator: logout,
  effect: (_action, api) => resetForUserChange(api.dispatch as AppDispatch),
});

// Switching tenant: reset tenant-scoped caches so the new organization never
// reads the previous one's rows out of the cache.
listenerMiddleware.startListening({
  actionCreator: setActiveOrganization,
  effect: (_action, api) => {
    for (const tenantApi of TENANT_SCOPED_APIS) {
      api.dispatch(tenantApi.util.resetApiState());
    }
  },
});

/**
 * Redux store configuration.
 */
export const store = configureStore({
  reducer: {
    auth: authReducer,
    organization: organizationReducer,
    [authApi.reducerPath]: authApi.reducer,
    [organizationApi.reducerPath]: organizationApi.reducer,
    [conversationsApi.reducerPath]: conversationsApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .prepend(listenerMiddleware.middleware)
      .concat(authApi.middleware)
      .concat(organizationApi.middleware)
      .concat(conversationsApi.middleware),
  devTools: import.meta.env.DEV,
});

// Re-read the persisted session on app start. Both slices already rehydrate at
// module load (so the first render knows whether there is a session); this
// covers storage written after the module was evaluated, e.g. by another tab.
store.dispatch(initializeAuth());
store.dispatch(initializeActiveOrganization());

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
